import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { PayService } from '../service/pay.service';

interface Pays {
  id?: number;
  nom: string;
  code: string;
  sigleMonnaie?: string;
}

interface SignUpPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  codePays: string;
  nidentification?: string;
}

type SignUpForm = {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  numeroMembres: FormControl<string>;
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
  codePays: FormControl<string>;
};

type SignUpControlName = keyof SignUpForm;

type DiaspoForm = {
  diaspophone: FormControl<string>;
};

const passwordMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const password = group.get('password')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  return password === confirmPassword ? null : { mismatch: true };
};

@Component({
  selector: 'app-singn-up',
  templateUrl: './singn-up.component.html',
  styleUrls: ['./singn-up.component.css'],
  standalone: false
})
export class SingnUpComponent implements OnInit {
  signUp!: FormGroup<SignUpForm>;

  emailexist = false;
  phonexist = false;
  loader = false;
  validation = false;
  letterchange = false;
  capitalchange = false;
  numberchange = false;
  lengthchange = false;
  symbolechange = false;
  diaspoForm?: FormGroup<DiaspoForm>;
  isCameroun = false;
  payslist: Pays[] = [];
  show = false;

  currentStep = 1;
  readonly totalSteps = 5;

  private readonly stepOneControls: SignUpControlName[] = [
    'numeroMembres'
  ];

  private readonly stepTwoControls: SignUpControlName[] = [
    'firstName',
    'lastName'
  ];

  private readonly stepThreeControls: SignUpControlName[] = [
    'email',
    'phone',
    'codePays'
  ];

  private readonly stepFourControls: SignUpControlName[] = [
    'password',
    'confirmPassword'
  ];

  constructor(private userService: PayService, private router: Router) { }

  ngOnInit() {
    window.localStorage.setItem('usercodeThisUserCOdeIsGode', '000000');
    this.ngOnSignUp();
    this.getPays();
  }

  getPays(): void {
    this.userService.getlistPays().pipe(take(1)).subscribe((data: Pays[]) => {
      this.payslist = data ?? [];
    });
  }

  ngOnSignUp(): void {
    this.signUp = new FormGroup<SignUpForm>({
      firstName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
      }),
      lastName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
      }),
      email: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email]
      }),
      phone: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('[1-9][0-9]*'),
          Validators.pattern('(^(65)[0-9]{1}[0-9]{6}|^(69|66|67|64)[0-9]{7})$')
        ]
      }),
      numeroMembres: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
          Validators.pattern('^[A-Za-z0-9_-]+$')
        ]
      }),
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&].{8,}')
        ]
      }),
      confirmPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&].{8,}')
        ]
      }),
      codePays: new FormControl<string>('237', {
        nonNullable: true,
        validators: [Validators.required]
      })
    }, { validators: passwordMatchValidator });
  }

  ngDiaspoPhone() {
    this.diaspoForm = new FormGroup<DiaspoForm>({
      diaspophone: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(3), Validators.maxLength(16), Validators.pattern('[1-9][0-9]*')]
      }),
    });
  }

  getVirtuelPhone(formdata: unknown) {
    return formdata;
  }

  showPassword() {
    this.show = !this.show;
  }

  nextStep(): void {
    if (!this.signUp) {
      return;
    }

    if (this.currentStep === 1) {
      this.markStepTouched(this.stepOneControls);
      if (!this.isStepOneValid()) {
        return;
      }
      this.currentStep = 2;
      return;
    }

    if (this.currentStep === 2) {
      this.markStepTouched(this.stepTwoControls);
      if (!this.isStepTwoValid()) {
        return;
      }
      this.currentStep = 3;
      return;
    }

    if (this.currentStep === 3) {
      this.markStepTouched(this.stepThreeControls);
      if (!this.isStepThreeValid()) {
        return;
      }
      this.currentStep = 4;
      return;
    }

    if (this.currentStep === 4) {
      this.markStepTouched(this.stepFourControls);
      this.signUp.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      if (!this.isStepFourValid()) {
        return;
      }
      this.currentStep = 5;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  onSubmit(): void {
    if (this.currentStep < this.totalSteps) {
      this.nextStep();
      return;
    }

    this.signUp.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    if (this.signUp.invalid || this.emailexist || this.phonexist) {
      this.signUp.markAllAsTouched();
      return;
    }

    const value = this.signUp.getRawValue();
    const payload: SignUpPayload = {
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      email: value.email.trim(),
      phone: value.phone.trim(),
      password: value.password,
      confirmPassword: value.confirmPassword,
      codePays: value.codePays,
      nidentification: value.numeroMembres.trim()
    };

    this.loader = true;
    this.userService.saveUser(payload).pipe(take(1)).subscribe(_response => {
      setTimeout(() => {
        this.loader = false;
        this.router.navigateByUrl('/connection');
      }, 500);
    }, _err => {
      setTimeout(() => {
        this.loader = false;
      }, 500);
    });
  }

  private markStepTouched(controlNames: SignUpControlName[]): void {
    controlNames.forEach(controlName => this.signUp.controls[controlName].markAsTouched());
  }

  private allControlsValid(controlNames: SignUpControlName[]): boolean {
    return controlNames.every(controlName => this.signUp.controls[controlName].valid);
  }

  isStepOneValid(): boolean {
    return this.allControlsValid(this.stepOneControls);
  }

  isStepTwoValid(): boolean {
    return this.allControlsValid(this.stepTwoControls);
  }

  isStepThreeValid(): boolean {
    return this.allControlsValid(this.stepThreeControls) && !this.emailexist && !this.phonexist;
  }

  isStepFourValid(): boolean {
    return this.allControlsValid(this.stepFourControls) && !this.signUp.hasError('mismatch');
  }

  stepProgress(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  get firstName() { return this.signUp.controls.firstName; }
  get lastName() { return this.signUp.controls.lastName; }
  get email() { return this.signUp.controls.email; }
  get phone() { return this.signUp.controls.phone; }
  get numeroMembres() { return this.signUp.controls.numeroMembres; }
  get password() { return this.signUp.controls.password; }
  get confirmPassword() { return this.signUp.controls.confirmPassword; }
  get codePays() { return this.signUp.controls.codePays; }

  isEmail(email: string | null): void {
    if (!email || this.email.invalid) {
      this.emailexist = false;
      return;
    }
    this.userService.findEmail(email).pipe(take(1)).subscribe(res => {
      this.emailexist = !res?.success;
    }, _ => this.emailexist = false);
  }

  isPhone(phone: string | null): void {
    if (!phone || this.phone.invalid) {
      this.phonexist = false;
      return;
    }
    this.userService.findPhone(phone).pipe(take(1)).subscribe(res => {
      this.phonexist = !res?.success;
    }, _ => this.phonexist = false);
  }

  showvalidationpart() {
    this.validation = true;
  }

  hidevalidationpart() {
    this.validation = false;
  }

  validateValue(passwordValue: string): void {
    const value = passwordValue || '';

    this.letterchange = /[a-z]/.test(value);
    this.capitalchange = /[A-Z]/.test(value);
    this.numberchange = /[0-9]/.test(value);
    this.lengthchange = value.length >= 8 && value.length < 17;
    this.symbolechange = /[$@$!%*?&]/.test(value);
  }
}
